import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(1.5rem, 5vw, 2.5rem)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '450px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#d4edda',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <CheckCircle size={32} style={{ color: '#28a745' }} />
          </div>
          
          <h2 style={{ fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', color: '#333', marginBottom: '1rem' }}>
            Check Your Email
          </h2>
          
          <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', color: '#666', marginBottom: '1.5rem', lineHeight: '1.6' }}>
            If an account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
          </p>
          
          <p style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', color: '#999', marginBottom: '2rem' }}>
            Don't see the email? Check your spam folder.
          </p>
          
          <Link 
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#4A90E2',
              textDecoration: 'none',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: 'clamp(1.5rem, 5vw, 2.5rem)',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '450px',
        width: '100%'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)', color: '#333', marginBottom: '0.5rem' }}>
            Forgot Password?
          </h1>
          <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', color: '#666' }}>
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              <Mail size={18} />
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              style={{
                width: '100%',
                padding: 'clamp(0.75rem, 3vw, 1rem)',
                fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
                border: '2px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4A90E2'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: 'clamp(0.85rem, 3vw, 1rem)',
              fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
              fontWeight: '600',
              color: 'white',
              backgroundColor: loading ? '#7fb3e8' : '#4A90E2',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              marginBottom: '1.5rem'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#357ABD')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#4A90E2')}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div style={{ textAlign: 'center' }}>
          <Link 
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#4A90E2',
              textDecoration: 'none',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
